import React, { useState, useRef, useEffect } from "react";
import { Play } from "lucide-react";
import Image from "../../../Tags/Image";
import AudioPlayer from "./AudioPlayer";

export default function CallTranscript({ utterances, callDetails, callMetadata, onDownload }) {
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [seekToTime, setSeekToTime] = useState(null);
  const transcriptContainerRef = useRef(null);
  const utteranceRefs = useRef({});
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerColor = (speaker) => {
    return speaker === "A" ? "text-[#6941C6] dark:text-[#B692F6]" : "text-pink-600 dark:text-[#BA24D5]";
  };

  const getSpeakerName = (speaker) => {
    return speaker === "A" ? (callDetails?.speaker_label?.A || "Speaker A") : (callDetails?.speaker_label?.B || "Speaker B");
  };

  const getSpeakerDot = (speaker) => {
    return speaker === "A" ? "bg-[#079455]" : "bg-[#D92D20]";
  };


  const getCurrentActiveUtterance = () => {
    if (!utterances || !currentAudioTime) return null;
    
    return utterances.find(utterance => 
      currentAudioTime >= utterance.start && currentAudioTime <= utterance.end
    );
  };

 
  useEffect(() => {
    const activeUtterance = getCurrentActiveUtterance();
    if (activeUtterance && transcriptContainerRef.current) {
      const utteranceElement = utteranceRefs.current[activeUtterance.start];
      if (utteranceElement) {
        utteranceElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [currentAudioTime, utterances]);


  useEffect(() => {
    if (seekToTime !== null) {
      const timer = setTimeout(() => {
        setSeekToTime(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [seekToTime]);

  const handleTimeUpdate = (timeMs) => {
    setCurrentAudioTime(timeMs);
  };

  const handleUtteranceClick = (startTime) => {
    setSeekToTime(startTime);
  };

  return (
    <div className="space-y-6">
      
       
         <AudioPlayer 
           audioUrl={callMetadata?.recording_url || callDetails?.recording_url} 
           callDetails={callDetails} 
           callMetadata={callMetadata}
           onTimeUpdate={handleTimeUpdate} 
           seekToTime={seekToTime} 
           onDownload={onDownload}
         />
      <div className="bg-white dark:bg-[#0C111D] rounded-lg border border-gray-200 dark:border-[#1F242F]">
      
        <div className="px-6 pt-3 dark:border-gray-700 ">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#414651] dark:text-white">
              Transcript
            </h2>
          </div>
        </div>
   
        <div ref={transcriptContainerRef} className="px-6 pb-4 max-h-[612px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="space-y-4">
            {utterances?.map((utterance, index) => {
              const isActive = currentAudioTime >= utterance.start && currentAudioTime <= utterance.end;
              
              return (
                <div 
                  key={index} 
                  ref={el => utteranceRefs.current[utterance.start] = el}
                  onClick={() => handleUtteranceClick(utterance.start)}
                  className={`group flex gap-4 mt-3 rounded-lg transition-all duration-200 cursor-pointer relative ${
                    isActive 
                      ? '' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-50'
                  }`}
                >
                 
                  <div className="flex-shrink-0 w-40">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-semibold text-sm ${getSpeakerColor(utterance.speaker)}`}>
                        {getSpeakerName(utterance.speaker)}
                      </span>
                      {/* <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getSpeakerDot(utterance.speaker)}`}></div>  */}
                    </div>
                    <div className={`text-base font-normal ${
                      isActive ? 'text-purple-600 dark:text-purple-400 font-semibold' : 'text-[#475467] dark:text-[#94969C]'
                    }`}>
                      {formatTime(utterance.start)}
                    </div>
                  </div>

                
                  <div className="flex-1 min-w-0 ml-4 relative">
                    <div className={`text-sm font-normal leading-relaxed ${
                      isActive 
                        ? 'text-[#CA8504] dark:text-[#CA8504] font-medium' 
                        : 'text-[#475467] dark:text-[#94969C]'
                    }`}>
                      {utterance.text}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}